import { useState, useRef, useEffect } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Download, Sparkles, Wand2, RefreshCw } from 'lucide-react';

export const BannerGeneratorPage = () => {
  usePageTitle('Banner Generator');

  const canvasRef = useRef(null);
  const [settings, setSettings] = useState({
    width: 1200,
    height: 400,
    tiltAngle: 8,
    fadeIntensity: 0.75,
    cornerRadius: 16,
    bgColor: '#0a0a0f',
    accentColor: '#00ff88',
    text: 'COMING SOON',
    matrixScale: 0.7,
    glowIntensity: 0.9,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    generateBanner();
  }, [settings]);

  const generateMatrixImage = () => {
    // Create offscreen canvas for matrix
    const matrixCanvas = document.createElement('canvas');
    matrixCanvas.width = 1400;
    matrixCanvas.height = 800;
    const ctx = matrixCanvas.getContext('2d');

    // Background
    const gradient = ctx.createLinearGradient(0, 0, 0, matrixCanvas.height);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, '#f8f9fa');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, matrixCanvas.width, matrixCanvas.height);

    // Matrix data
    const names = ['John Smith', 'Sarah Johnson', 'Mike Wilson', 'Emma Davis', 'James Brown', 'Lisa Anderson', 'David Taylor', 'Sophie Martin'];
    const requirements = ['CSCS Card', 'First Aid', 'Manual Handling', 'Fire Safety', 'Working at Height', 'Confined Spaces', 'Asbestos Awareness'];

    const cellWidth = 160;
    const cellHeight = 70;
    const headerHeight = 80;
    const nameColumnWidth = 180;
    const startX = 40;
    const startY = 60;

    // Draw title
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 32px Arial, sans-serif';
    ctx.fillText('Training Compliance Matrix', startX, 40);

    // Draw header row
    ctx.fillStyle = '#2d3748';
    ctx.fillRect(startX, startY, nameColumnWidth + requirements.length * cellWidth, headerHeight);

    // Draw header text
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Name column header
    ctx.fillText('Team Member', startX + nameColumnWidth / 2, startY + headerHeight / 2);

    // Requirement headers
    requirements.forEach((req, i) => {
      const x = startX + nameColumnWidth + i * cellWidth + cellWidth / 2;
      const y = startY + headerHeight / 2;

      // Split text if too long
      const words = req.split(' ');
      if (words.length > 2) {
        ctx.fillText(words.slice(0, 2).join(' '), x, y - 8);
        ctx.fillText(words.slice(2).join(' '), x, y + 8);
      } else {
        ctx.fillText(req, x, y);
      }
    });

    // Draw data rows
    names.forEach((name, rowIndex) => {
      const y = startY + headerHeight + rowIndex * cellHeight;

      // Alternating row colors
      ctx.fillStyle = rowIndex % 2 === 0 ? '#ffffff' : '#f7fafc';
      ctx.fillRect(startX, y, nameColumnWidth + requirements.length * cellWidth, cellHeight);

      // Draw borders
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      ctx.strokeRect(startX, y, nameColumnWidth + requirements.length * cellWidth, cellHeight);

      // Draw name
      ctx.fillStyle = '#2d3748';
      ctx.font = '16px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(name, startX + 15, y + cellHeight / 2);

      // Draw requirement cells
      requirements.forEach((_, colIndex) => {
        const x = startX + nameColumnWidth + colIndex * cellWidth;
        const isCompliant = Math.random() > 0.25;

        // Cell background
        if (isCompliant) {
          ctx.fillStyle = '#d4edda';
        } else {
          ctx.fillStyle = '#f8d7da';
        }
        ctx.fillRect(x + 2, y + 2, cellWidth - 4, cellHeight - 4);

        // Draw icon
        ctx.textAlign = 'center';
        ctx.font = 'bold 32px Arial, sans-serif';

        if (isCompliant) {
          ctx.fillStyle = '#28a745';
          ctx.fillText('✓', x + cellWidth / 2, y + cellHeight / 2 + 2);
        } else {
          ctx.fillStyle = '#dc3545';
          ctx.fillText('✗', x + cellWidth / 2, y + cellHeight / 2 + 2);
        }

        // Draw border
        ctx.strokeStyle = '#e2e8f0';
        ctx.strokeRect(x, y, cellWidth, cellHeight);
      });
    });

    return matrixCanvas;
  };

  const generateBanner = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { width, height, cornerRadius, bgColor, accentColor, tiltAngle, fadeIntensity, text, matrixScale, glowIntensity } = settings;

    // Set canvas size with high DPI
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    ctx.scale(dpr, dpr);

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Clip to rounded rectangle
    ctx.save();
    roundRectPath(ctx, 0, 0, width, height, cornerRadius);
    ctx.clip();

    // Background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Subtle grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.02)';
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 40) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 40) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    // Gradient overlay
    const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, width * 0.7);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.4)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Generate matrix
    const matrixImage = generateMatrixImage();

    // Draw isometric matrix
    ctx.save();

    const centerX = width / 2;
    const centerY = height / 2;

    const matrixWidth = width * matrixScale;
    const matrixHeight = (matrixImage.height / matrixImage.width) * matrixWidth;

    ctx.translate(centerX, centerY);
    ctx.rotate((tiltAngle * Math.PI) / 180);

    // Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetX = 20;
    ctx.shadowOffsetY = 20;

    // Draw matrix with perspective
    ctx.drawImage(matrixImage, -matrixWidth / 2, -matrixHeight / 2, matrixWidth, matrixHeight);

    // Glow effect
    if (glowIntensity > 0) {
      ctx.shadowColor = accentColor;
      ctx.shadowBlur = 60 * glowIntensity;
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 3;
      ctx.strokeRect(-matrixWidth / 2, -matrixHeight / 2, matrixWidth, matrixHeight);
    }

    ctx.restore();

    // Vignette fade
    const vignetteGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, width * 0.7);
    vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    vignetteGradient.addColorStop(0.6, `rgba(0, 0, 0, ${fadeIntensity * 0.3})`);
    vignetteGradient.addColorStop(1, `rgba(0, 0, 0, ${fadeIntensity})`);
    ctx.fillStyle = vignetteGradient;
    ctx.fillRect(0, 0, width, height);

    // Text
    if (text) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Glow
      ctx.shadowColor = accentColor;
      ctx.shadowBlur = 30 * glowIntensity;

      ctx.fillStyle = accentColor;
      ctx.font = `bold ${width * 0.08}px Arial, sans-serif`;
      ctx.fillText(text, width / 2, height - height * 0.15);

      // Second pass for extra glow
      ctx.shadowBlur = 50 * glowIntensity;
      ctx.fillText(text, width / 2, height - height * 0.15);
    }

    // Scanline effect
    ctx.shadowBlur = 0;
    for (let i = 0; i < height; i += 4) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
      ctx.fillRect(0, i, width, 2);
    }

    // Corner accents
    const accentSize = 40;
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 3;
    ctx.shadowColor = accentColor;
    ctx.shadowBlur = 15;

    // Top left
    ctx.beginPath();
    ctx.moveTo(cornerRadius + accentSize, cornerRadius);
    ctx.lineTo(cornerRadius, cornerRadius);
    ctx.lineTo(cornerRadius, cornerRadius + accentSize);
    ctx.stroke();

    // Top right
    ctx.beginPath();
    ctx.moveTo(width - cornerRadius - accentSize, cornerRadius);
    ctx.lineTo(width - cornerRadius, cornerRadius);
    ctx.lineTo(width - cornerRadius, cornerRadius + accentSize);
    ctx.stroke();

    // Bottom left
    ctx.beginPath();
    ctx.moveTo(cornerRadius, height - cornerRadius - accentSize);
    ctx.lineTo(cornerRadius, height - cornerRadius);
    ctx.lineTo(cornerRadius + accentSize, height - cornerRadius);
    ctx.stroke();

    // Bottom right
    ctx.beginPath();
    ctx.moveTo(width - cornerRadius - accentSize, height - cornerRadius);
    ctx.lineTo(width - cornerRadius, height - cornerRadius);
    ctx.lineTo(width - cornerRadius, height - cornerRadius - accentSize);
    ctx.stroke();

    ctx.restore();
  };

  const roundRectPath = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSettings({ ...settings, screenshotUrl: event.target.result });
        setImageLoaded(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const downloadBanner = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = 'bohsa-christmas-banner.png';
    link.href = canvas.toDataURL('image/png', 1.0);
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Wand2 className="h-10 w-10 text-emerald-400 animate-pulse" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              BOHSA Banner Generator
            </h1>
            <Sparkles className="h-10 w-10 text-cyan-400 animate-pulse" />
          </div>
          <p className="text-gray-400 text-lg">Create a sleek banner for the Training Matrix reveal</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Preview */}
          <div className="lg:col-span-2">
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Banner Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-950 rounded-lg p-8 flex items-center justify-center border border-slate-700">
                  <canvas
                    ref={canvasRef}
                    className="max-w-full h-auto shadow-2xl rounded-lg"
                  />
                </div>
                <div className="mt-6 flex gap-3">
                  <Button
                    onClick={downloadBanner}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PNG
                  </Button>
                  <Button
                    onClick={() => generateBanner()}
                    variant="outline"
                    className="border-slate-600 text-slate-200 hover:bg-slate-800"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Regenerate
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Dimensions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Width (px)</Label>
                  <Input
                    type="number"
                    value={settings.width}
                    onChange={(e) => setSettings({ ...settings, width: parseInt(e.target.value) })}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Height (px)</Label>
                  <Input
                    type="number"
                    value={settings.height}
                    onChange={(e) => setSettings({ ...settings, height: parseInt(e.target.value) })}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Matrix Effects</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Tilt: {settings.tiltAngle}°</Label>
                  <Slider
                    value={[settings.tiltAngle]}
                    onValueChange={([value]) => setSettings({ ...settings, tiltAngle: value })}
                    min={0}
                    max={20}
                    step={1}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Scale: {(settings.matrixScale * 100).toFixed(0)}%</Label>
                  <Slider
                    value={[settings.matrixScale]}
                    onValueChange={([value]) => setSettings({ ...settings, matrixScale: value })}
                    min={0.5}
                    max={0.9}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Glow: {(settings.glowIntensity * 100).toFixed(0)}%</Label>
                  <Slider
                    value={[settings.glowIntensity]}
                    onValueChange={([value]) => setSettings({ ...settings, glowIntensity: value })}
                    min={0}
                    max={1}
                    step={0.1}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Fade: {(settings.fadeIntensity * 100).toFixed(0)}%</Label>
                  <Slider
                    value={[settings.fadeIntensity]}
                    onValueChange={([value]) => setSettings({ ...settings, fadeIntensity: value })}
                    min={0.3}
                    max={1}
                    step={0.05}
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Style</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Background</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings.bgColor}
                      onChange={(e) => setSettings({ ...settings, bgColor: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={settings.bgColor}
                      onChange={(e) => setSettings({ ...settings, bgColor: e.target.value })}
                      className="flex-1 bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Accent Color</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      type="color"
                      value={settings.accentColor}
                      onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={settings.accentColor}
                      onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                      className="flex-1 bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Text</Label>
                  <Input
                    value={settings.text}
                    onChange={(e) => setSettings({ ...settings, text: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="COMING SOON"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-950/50 to-cyan-950/50 border-emerald-700/50">
              <CardHeader>
                <CardTitle className="text-emerald-400">Quick Presets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full border-emerald-600 text-emerald-300 hover:bg-emerald-900/50"
                  onClick={() => setSettings({
                    ...settings,
                    bgColor: '#0a0a0f',
                    accentColor: '#00ff88',
                    text: 'COMING SOON'
                  })}
                >
                  💚 Matrix Green
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-cyan-600 text-cyan-300 hover:bg-cyan-900/50"
                  onClick={() => setSettings({
                    ...settings,
                    bgColor: '#0f0a1a',
                    accentColor: '#00d4ff',
                    text: 'COMING SOON'
                  })}
                >
                  💙 Cyber Blue
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-pink-600 text-pink-300 hover:bg-pink-900/50"
                  onClick={() => setSettings({
                    ...settings,
                    bgColor: '#1a0a14',
                    accentColor: '#ff0080',
                    text: 'COMING SOON'
                  })}
                >
                  💗 Neon Pink
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
